import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTitle } from 'react-use';
import { toast } from 'sonner';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useReportEmptyState } from '@/hooks/useReportEmptyState';
import { getPermissions } from '@/redux/permissions/selectors';
import { setSidebarContent, setSidebarMeta } from '@/redux/sidebar/actions';
import { selectSidebar } from '@/redux/sidebar/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { EVENT_NOTIFICATIONS_DOCS_URL } from '@/util/constants';

import { DeletePluginModal } from './DeletePluginModal';
import { EmptyEventNotifications } from './EmptyEventNotifications';
import { PluginsTable } from './PluginsTable';
import usePlugins from '../../hooks/usePlugins';
import { DEV_TOOLS_SIDEBAR_KEYS, Plugin } from '../../types';

interface PluginsProps {
  onEmptyStateChange: (isEmpty: boolean) => void;
}

export const Plugins = ({ onEmptyStateChange }: PluginsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sidebarPluginId = useSelector((state: RootState) => selectSidebar(state).meta?.pluginId);
  const { plugins, listStatus, updatePlugin } = usePlugins();
  const [search, setSearch] = useState('');
  const [deletePluginId, setDeletePluginId] = useState<string | null>(null);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'plugins', actions.WRITE)
  );
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'plugins', actions.DELETE)
  );

  useTitle(t('pages.devtool_event_notifications'));

  const handleCreate = useCallback(() => {
    dispatch(setSidebarContent(DEV_TOOLS_SIDEBAR_KEYS.plugins));
  }, [dispatch]);

  const handleEdit = useCallback(
    (plugin: Plugin) => {
      dispatch(setSidebarContent(DEV_TOOLS_SIDEBAR_KEYS.plugins));
      dispatch(setSidebarMeta({ pluginId: plugin.plugin_id }));
    },
    [dispatch]
  );

  const handleDelete = useCallback((plugin: Plugin) => {
    setDeletePluginId(plugin.plugin_id);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeletePluginId(null);
  }, []);

  const handleDeleted = useCallback(
    (id: string) => {
      if (id === sidebarPluginId) {
        dispatch(setSidebarContent(null));
      }
    },
    [sidebarPluginId, dispatch]
  );

  const handleToggleActive = useCallback(
    (plugin: Plugin) => {
      updatePlugin(
        { id: plugin.plugin_id, data: { active: !plugin.active } },
        {
          onError: (error) => {
            toast.error(error.message);
          },
        }
      );
    },
    [updatePlugin]
  );

  const filteredPlugins = useMemo(() => {
    if (!search.trim()) {
      return plugins;
    }
    const query = search.toLowerCase();
    return plugins.filter(
      (plugin) =>
        plugin.name.toLowerCase().includes(query) || plugin.url.toLowerCase().includes(query)
    );
  }, [plugins, search]);

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'plugins',
    isLoading,
    isEmpty: plugins.length === 0,
    isError: listStatus === 'error',
  });

  useReportEmptyState(isEmptyPage, onEmptyStateChange);

  return (
    <>
      <ContextualHelp
        title={t('developer_tools.contextual_help.event_notifications_title')}
        name="about_event_notifications"
        links={[{ label: t('common.learn_more'), url: EVENT_NOTIFICATIONS_DOCS_URL }]}
      >
        {t('developer_tools.contextual_help.event_notifications_message')}
      </ContextualHelp>

      {showEmpty && <EmptyEventNotifications onCreate={handleCreate} canWrite={canWrite} />}

      {showEmptySkeleton && <EmptySkeleton descriptionLines={3} />}

      {showList && (
        <>
          <div className="mb-4 pl-1">
            <SearchBar
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('plugins.search_event')}
            />
          </div>

          <PluginsTable
            plugins={filteredPlugins}
            isLoading={isLoading}
            canWrite={canWrite}
            canDelete={canDelete}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            activeRowId={sidebarPluginId || undefined}
          />
        </>
      )}
      {deletePluginId && (
        <DeletePluginModal
          pluginId={deletePluginId}
          onClose={handleDeleteClose}
          onDeleted={handleDeleted}
        />
      )}
    </>
  );
};
